import React, { useEffect, useRef, useState } from 'react';
import { MultiSelectOption } from '../types';

type DistributiveDropdownProps = {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
};

const DistributiveDropdown: React.FC<DistributiveDropdownProps> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current?.contains(event.target as Node) || menuRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-normal text-slate-900 shadow-inner shadow-slate-100 transition focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
      >
        {selected.length === 0 ? (
          <span className="text-slate-500 w-12"></span>
        ) : (
          <span className="flex flex-wrap items-center gap-1">
            {selected.map((value) => (
              <span
                key={value}
                className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white"
              >
                {value}
              </span>
            ))}
          </span>
        )}
        <span className="text-xs text-slate-500">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          role="listbox"
          aria-multiselectable
          className="absolute right-0 z-10 mt-2 w-22 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_18px_50px_-30px_rgba(15,23,42,0.45)]"
        >
          {options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">Add distributives!</p>
          ) : (
            <ul className="max-h-60 overflow-auto py-1">
              {options.map((option) => (
                <li key={option.value}>
                  <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-slate-800 transition hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selected.includes(option.value)}
                      onChange={() => toggleValue(option.value)}
                      className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                    <span>{option.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default DistributiveDropdown;
